import { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LuPencil } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../components/CustomModal';
import MultiLineText from '../../components/MultiLineText';
import NewTableComponent from '../../components/NewTableComponent';
import Button from '../../components/button';
import { deletePermission, fetchPermissionData, getAllPermissionsDropdown } from '../../store/Slices/PermissionSlice';
import { dateMonthYear } from '../../utils/helper';
import { errorNotify, successNotify } from '../../utils/toast';
import AddPermissionGroup from './PermissionGroup/AddPermissionGroup';
import EditPermissionGroup from './PermissionGroup/EditPermissionGroup';

const PermissionGroup = () => {
    const dispatch = useDispatch();

    const { data = [], meta, loading, deleteLoading } = useSelector((state) => state.permissions);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(meta?.itemsPerPage || 10);
    const [addPermissionPopUp, setAddPermissionPopUp] = useState(false);
    const [editPermissionPopUp, setEditPermissionPopUp] = useState(false);
    const [editPermissionId, setEditPermissionId] = useState(null);
    const [deletePermissionPopup, setDeletePermissionPopup] = useState(false);
    const [deletePermissionId, setDeletePermissionId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchFilters, setSearchFilters] = useState(null);

    const columns = [
        {
            name: "Name",
            key: "name",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: true,
            selector: (row) => row?.name,
            modifyData: (row) => row?.name,
            sortable: true,
            hasIcon: false,
            tableColumn: true,
            width: "500px",
            filterName: "nameFilter",
            type: "text",
        },
        {
            name: "No. of Users",
            key: "permission_users_count",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: true,
            selector: (row) => row?.permission_users_count ? row?.permission_users_count : 0,
            modifyData: (row) => row?.permission_users_count ? row?.permission_users_count : 0,
            sortable: true,
            hasIcon: false,
            tableColumn: false
        },
        {
            name: "Permissions",
            key: "permission_badges",
            tableColumn: true,
            filterName: "permissionBadgeNameFilter",
            type: "text",
            selector: (row) => {
                const permissionItems = row?.permission_badges || [];

                if (permissionItems.length === 0) {
                    return <span className="no-items">No Permission Group</span>;
                }

                if (permissionItems.length === 1) {
                    return (
                        <>
                            {
                                permissionItems[0]?.badge ? <div className='service-task'>
                                    {permissionItems[0]?.badge}
                                </div> : <span className="no-items">No Permission Group</span>
                            }
                        </>
                    );
                }

                const firstItem = permissionItems[0]?.badge || 'N/A';
                const remainingCount = permissionItems.length - 1;

                return (
                    <>
                        {
                            firstItem === 'N/A' ? <span className="no-items">No Permission Group</span> :
                                <div className="service-tasks-container">
                                    <span className="first-task">
                                        <MultiLineText text={firstItem} maxWidth={"90px"} />
                                    </span>

                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip>
                                                <div>
                                                    {permissionItems.slice(1).map((item, idx) => (
                                                        <div key={idx} className='tooltip_item'>{item?.badge}</div>
                                                    ))}
                                                </div>
                                            </Tooltip>
                                        }
                                    >
                                        <span className="additional-tasks">+{remainingCount}</span>
                                    </OverlayTrigger>
                                </div>
                        }
                    </>
                );
            },
            width: "300px",
        },
        {
            name: "Date of Creation",
            key: "DoC",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: true,
            selector: (row) => row?.created_at ? dateMonthYear(row?.created_at) : "",
            modifyData: (row) => row?.created_at ? dateMonthYear(row?.created_at) : "",
            sortable: true,
            hasIcon: false,
            tableColumn: true,
            filterName: "creationDateFilter",
            type: "date"
        },
        {
            name: "Actions",
            key: "actions",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: false,
            selector: (row) => (
                <div className="table_action_btn">
                    <button
                        className="edit_btn"
                        onClick={() => {
                            setEditPermissionPopUp(true);
                            setEditPermissionId(row?.id);
                        }}
                    >
                        <LuPencil />
                    </button>
                    <button onClick={() => {
                        if (row?.permission_users_count > 0) {
                            errorNotify('Unable to complete action: Users already assigned to this permission group');
                            return;
                        }
                        setDeletePermissionPopup(true);
                        setDeletePermissionId(row?.id);
                    }} className="delete_btn">
                        <RiDeleteBinLine />
                    </button>
                </div>
            ),
            sortable: false,
            width: "120px",
            tableColumn: false
        }
    ]

    useEffect(() => {
        dispatch(getAllPermissionsDropdown());
    }, [])

    useEffect(() => {
        setCurrentPage(1)
    }, [limit])

    const handleSearch = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (searchText.length > 0 || searchFilters) {
            dispatch(fetchPermissionData({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }))
        }
        else {
            dispatch(fetchPermissionData({
                page: currentPage,
                limit: limit
            }))
        }
    }, [searchText, currentPage, limit, searchFilters]);

    const handleLimitChange = (newLimit) => {
        setCurrentPage(1);
        setLimit(newLimit);
    };


    const handleDeletePermission = async () => {
        try {
            await dispatch(deletePermission(deletePermissionId)).unwrap();
            successNotify('Permission deleted successfully');
            setDeletePermissionPopup(false);
            setDeletePermissionId(null);

            dispatch(fetchPermissionData({
                page: currentPage,
                limit: limit,
                searchFilters: searchFilters
            }));
        } catch (error) {
            errorNotify(error);
        }
    };

    return (
        <div>
            <AddPermissionGroup show={addPermissionPopUp} onHide={() => setAddPermissionPopUp(false)} currentPage={currentPage} limit={limit} />

            <EditPermissionGroup
                show={editPermissionPopUp}
                onHide={() => {
                    setEditPermissionPopUp(false);
                    setEditPermissionId(null);
                }}
                currentPage={currentPage}
                limit={limit}
                permissionGroupId={editPermissionId}
                searchFilters={searchFilters}
            />

            <CustomModal show={deletePermissionPopup} handleClose={() => setDeletePermissionPopup(false)} size="md" heading="Delete Permission Group" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this Permission Group?</p>

                    <Button
                        cancelBtn={() => setDeletePermissionPopup(false)}
                        submitBtn={handleDeletePermission}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                    />
                </div>
            </CustomModal>

            <NewTableComponent
                btnName="Add Permission Group"
                pageName={"permission_group"}
                showAddBtn={true}
                onAddClick={() => setAddPermissionPopUp(true)}
                columns={columns} data={data}
                onSearch={handleSearch}
                loading={loading}
                currentPage={currentPage} setCurrentPage={setCurrentPage}
                limit={limit} setLimit={handleLimitChange}
                totalItems={meta?.totalItems || 0}
                totalPages={meta?.totalPages || 1}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
            />
        </div>
    )
}

export default PermissionGroup